/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {Fragment, useEffect, useRef, useState} from "react";
import clsx from "clsx";
import {Listbox} from "@headlessui/react";
import {CheckIcon} from "@heroicons/react/24/solid";
import {ExclamationCircleIcon} from "@heroicons/react/24/outline";
import T from "components/T";
import {PhoneCountry} from "utils/countryUtils";
import {useTranslation} from "react-i18next";
import {Overlay} from "react-overlays";
import useGetCountryCode from "utils/hooks/useGetCountryCode";
import FeatherIcon from "components/FeatherIcon";

interface CountrySelectCountryComponentProps {
    countryCode?: string;
}

const CountrySelectCountryComponent = (props: CountrySelectCountryComponentProps) => {
    const [countryFlag, setCountryFlag] = useState(null);

    useEffect(() => {
        const loadCountryFlag = async () => {
            try {
                const countryFlagModule = await import(`components/CountryIcons/3x2/${props.countryCode}.svg`);
                setCountryFlag(countryFlagModule.default);
            } catch (error) {
                console.error(`Error loading country flag for ${props.countryCode}:`, error);
                // Handle error (e.g., display a placeholder flag)
            }
        };

        if (props.countryCode) {
            loadCountryFlag().then(r => {});
        }

        // Clean up on unmount
        return () => {
            setCountryFlag(null);
        };
    }, [props.countryCode]);

    return (
        <div className={"CountryFlag min-w-[45px] w-[45px] min-h-[30px] h-[30px] border border-arbo-borderGray"}>
            {countryFlag && <img alt={`${props.countryCode} flag`} src={countryFlag} />}
        </div>
    );
};

interface ReservationDetailCountryProps {
    options: PhoneCountry[];
    defaultCountry: any;
    value?: any;
    register: any;
    name: string;
    label: string;
    className?: string;
    classNameContainer?: string;
    classNameInput?: string;
    required?: boolean;
    errors?: any;
    disabled?: boolean;
    setValue?: any;
    style?: "normal" | "inline";
    resetValue?: boolean;
    setResetCountry?: (val: boolean) => void;
}

const ReservationDetailCountry = (props: ReservationDetailCountryProps) => {
    const {t} = useTranslation();
    const getCountryCode = useGetCountryCode();
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry | null>(null);
    const selectedOptionRef = useRef<HTMLLIElement>(null);
    const targetRef = useRef(null);
    const [show, setShow] = useState(false);
    const options = props?.options;

    const searchCountryByName = (countryCode: string): PhoneCountry => {
        const countryIndex = props.options.findIndex(country => country.textCode == countryCode);
        return props.options[countryIndex];
    };

    const setSelectedCountryHandler = (country: PhoneCountry) => {
        setSelectedCountry(country);
        setShow(false);
        if (country) {
            props.setValue(props.name, country.name);
        }
    };

    const checkCountry = async (name: string) => {
        const checkedCountryCode = await getCountryCode.getCountryCodeFromCountryName(name);
        // if checkedCountryCode not search code, set default country code
        const searchedCountry = searchCountryByName(checkedCountryCode ? checkedCountryCode : props.defaultCountry);
        setSelectedCountryHandler(searchedCountry);
    };

    useEffect(() => {
        if (props?.value) {
            checkCountry(props.value).then(r => {});
        }
        if (!props?.value) {
            setSelectedCountryHandler(searchCountryByName(props.defaultCountry));
        }
    }, [props?.value]);

    useEffect(() => {
        if (props?.resetValue && props?.setResetCountry) {
            props?.value ? checkCountry(props.value).then(r => {}) : setSelectedCountryHandler(searchCountryByName(props.defaultCountry));
            props.setResetCountry(false);
        }
    }, [props?.resetValue]);

    return (
        <div
            className={clsx(
                "ReservationDetailCountry w-full relative",
                props?.style === "inline" && "sm:flex sm:flex-row sm:space-x-6 sm:items-center sm:flex-wrap",
                props.className
            )}
        >
            <label
                className={clsx(
                    "font-medium mb-1.5 flex text-base md:text-base text-black",
                    // props?.style === "inline" && "sm:!w-1/3 sm:justify-end sm:flex-1",
                    props.errors?.[props.name] ? "text-red-500" : ""
                )}
                htmlFor={props.name}
            >
                <span>{t(props.label)}</span>
                {props.required && <em className={"text-red-500"}>*</em>}
            </label>

            <div className={clsx("flex flex-row relative w-full flex-1", props?.style === "inline" && "sm:!w-2/3 sm:!flex-none")}>
                <Listbox disabled={props.disabled} value={selectedCountry} onChange={setSelectedCountryHandler}>
                    {({open}) => (
                        <div className="w-full">
                            <Listbox.Button
                                ref={targetRef}
                                className={clsx(
                                    "z-10 h-full flex items-center justify-between w-full pl-3 py-2 rounded-[5px] border-arbo-borderGray border text-base md:text-base font-medium",
                                    props.disabled
                                        ? selectedCountry
                                            ? "bg-gray-100 text-gray-600"
                                            : "bg-gray-100 text-gray-400"
                                        : selectedCountry
                                          ? "bg-white text-black"
                                          : "bg-white text-gray-400",
                                    selectedCountry ? "text-black border-arbo-borderGray" : "text-gray-400",
                                    props.errors?.[props.name] && !open && !selectedCountry && "border-red-500"
                                )}
                                onClick={() => setShow(!show)}
                            >
                                <div className={"flex flex-row items-center"}>
                                    {/*<CountrySelectCountryComponent countryCode={selectedCountry?.textCode} />*/}
                                    {/*<span className={clsx("ml-2 block truncate")}>{selectedCountry?.name}</span>*/}
                                    <span className={clsx("block truncate")}>{selectedCountry?.name}</span>
                                </div>
                                <span className="pointer-events-none flex h-full pl-1 pr-1 items-center">
                                    {/*<ChevronDownIcon className={"w-4 h-4 stroke-2 text-black"} />*/}
                                    <FeatherIcon name={"chevron-down"} strokeWidth={2} className={"size-6 flex-none text-black"} />
                                </span>
                            </Listbox.Button>
                            <Overlay placement="bottom-start" show={show} target={targetRef.current} rootClose onHide={() => setShow(false)}>
                                {({props, placement}) => (
                                    <Listbox.Options
                                        {...props}
                                        style={{
                                            ...props.style,
                                            transition: "maxHeight 0.3s ease-in-out",
                                            // @ts-ignore
                                            width: targetRef?.current ? targetRef.current.getBoundingClientRect().width : "100%",
                                        }}
                                        className={clsx(
                                            "transition-all duration-300 ease-in h-full drop-shadow shadow-sm z-10 max-h-[15rem] w-full max-w-full rounded-md bg-white overflow-x-hidden py-1 text-base",
                                            placement
                                        )}
                                        unmount={false}
                                        onLoad={() => {
                                            if (selectedCountry && selectedOptionRef?.current) {
                                                selectedOptionRef.current.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "nearest",
                                                });
                                            }
                                        }}
                                    >
                                        {options.map(option => (
                                            <Listbox.Option key={option.textCode} as={Fragment} value={option}>
                                                {({selected, active}) => (
                                                    <li
                                                        className={clsx(
                                                            "flex flex-row cursor-pointer select-none py-1 pr-0 text-sm w-full",
                                                            active || selected ? "bg-gray-200/80 text-black" : ""
                                                        )}
                                                    >
                                                        {selected && (
                                                            <span className={"w-5 flex items-center"}>
                                                                <CheckIcon className={"w-3.5 h-3.5 ml-0.5"} />
                                                            </span>
                                                        )}
                                                        <div className={clsx("flex items-center truncate w-full", !selected && "pl-5")}>
                                                            <CountrySelectCountryComponent countryCode={option.textCode} />
                                                            <span className={clsx("ml-1 block truncate")}>{option.name}</span>
                                                        </div>
                                                    </li>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                )}
                            </Overlay>
                        </div>
                    )}
                </Listbox>
                <div className={"relative grow flex flex-row"}>
                    <input
                        {...props.register(props.name)}
                        className={clsx(
                            "block w-full px-3 py-2 rounded-md border-gray-200 border",
                            props.classNameInput,
                            props.errors?.[props.name] && "border-red-500",
                            props.disabled ? "bg-gray-100 text-gray-600" : "bg-white"
                        )}
                        defaultValue={props?.value}
                        disabled={props.disabled}
                        name={props.name}
                        required={props.required}
                        type={"hidden"}
                        id={props.name}
                    />
                </div>
            </div>
            {props.errors?.[props.name]?.message && (
                <div className={clsx("text-red-500 text-sm mt-1 flex flex-row items-center shrink-0")}>
                    <div className={clsx("shrink-0 flex flex-row items-center text-xxs md:text-base w-full")}>
                        <ExclamationCircleIcon className={"size-[14px] md:size-[18px] flex-none mr-0.5"} />
                        <T>{props.errors?.[props.name]?.message}</T>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationDetailCountry;
